import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";

/** Clean cell values for Excel (no raw JSON / ISO noise). */
export function formatExcelCellValue(value) {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) {
    return value.toISOString().split("T")[0];
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value
        .map((v) => formatExcelCellValue(v))
        .filter((s) => s !== "")
        .join(", ");
    }
    return "";
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}T\d/.test(s)) return s.split("T")[0];
  return s;
}

export function humanizeExcelHeader(key) {
  const k = String(key || "").trim();
  if (!k) return "";
  return k
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Long numeric strings (CNIC, phone, GR) stay text; shorter values may become numbers. */
function cellValueForSheet(key, raw) {
  const text = formatExcelCellValue(raw);
  if (text === "") return { t: "s", v: "" };

  const keyHint = String(key || "").toLowerCase();
  const forceText =
    /cnic|contact|phone|mobile|rfid|account|card|bform|family|fcode|grno|gr_no|^gr$|empcode|gender|name/i.test(
      keyHint
    );

  if (!forceText && /^-?\d+(\.\d+)?$/.test(text) && text.length <= 11) {
    return { t: "n", v: Number(text) };
  }
  return { t: "s", v: text };
}

const STYLED_STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="0"/>
<fonts count="2">
<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/><scheme val="minor"/></font>
<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>
</fonts>
<fills count="3">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FF2F5496"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="2">
<border><left/><right/><top/><bottom/><diagonal/></border>
<border>
<left style="thin"><color rgb="FFB4B4B4"/></left>
<right style="thin"><color rgb="FFB4B4B4"/></right>
<top style="thin"><color rgb="FFB4B4B4"/></top>
<bottom style="thin"><color rgb="FFB4B4B4"/></bottom>
</border>
</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="3">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
<dxfs count="0"/>
<tableStyles count="0" defaultTableStyle="TableStyleMedium9" defaultPivotStyle="PivotStyleMedium4"/>
</styleSheet>`;

function colLetter(colIndex) {
  let n = colIndex + 1;
  let s = "";
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/**
 * Inject header fill, borders, and freeze pane into a SheetJS xlsx buffer (community xlsx has no cell styles).
 */
async function applyProfessionalXlsxPresentation(buffer, headerRow1Based) {
  const zip = await JSZip.loadAsync(buffer);
  const sheetPath = Object.keys(zip.files).find(
    (p) => /^xl\/worksheets\/sheet\d+\.xml$/.test(p) && !p.includes("_rels")
  );
  if (!sheetPath) return buffer;

  let sheetXml = await zip.file(sheetPath).async("string");
  const headerRow = String(headerRow1Based);

  sheetXml = sheetXml.replace(
    new RegExp(`<row r="${headerRow}">([\\s\\S]*?)</row>`),
    (match, inner) => {
      const styled = inner.replace(
        /<c r="([^"]+)"([^>]*)>/g,
        (cellOpen, ref, attrs) => {
          if (/\ss="/.test(attrs)) return `<c r="${ref}"${attrs}>`;
          return `<c r="${ref}" s="1"${attrs}>`;
        }
      );
      return `<row r="${headerRow}" ht="22" customHeight="1">${styled}</row>`;
    }
  );

  sheetXml = sheetXml.replace(
    /<row r="(\d+)">([\s\S]*?)<\/row>/g,
    (match, rowNum, inner) => {
      if (Number(rowNum) <= Number(headerRow)) return match;
      const styled = inner.replace(
        /<c r="([^"]+)"([^>]*)>/g,
        (cellOpen, ref, attrs) => {
          if (/\ss="/.test(attrs)) return `<c r="${ref}"${attrs}>`;
          return `<c r="${ref}" s="2"${attrs}>`;
        }
      );
      return `<row r="${rowNum}">${styled}</row>`;
    }
  );

  const freezeCell = `${colLetter(0)}${headerRow1Based + 1}`;
  const sheetViewInner = `<sheetViews><sheetView tabSelected="1" workbookViewId="0"><pane ySplit="${headerRow1Based}" topLeftCell="${freezeCell}" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft" activeCell="${freezeCell}" sqref="${freezeCell}"/></sheetView></sheetViews>`;

  if (/<sheetViews>/.test(sheetXml)) {
    sheetXml = sheetXml.replace(/<sheetViews>[\s\S]*?<\/sheetViews>/, sheetViewInner);
  } else {
    sheetXml = sheetXml.replace(
      /<worksheet[^>]*>/,
      (open) => `${open}${sheetViewInner}`
    );
  }

  zip.file("xl/styles.xml", STYLED_STYLES_XML);
  zip.file(sheetPath, sheetXml);
  return zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
}

/**
 * @param {Object[]} rows
 * @param {{ columns?: {key:string, label?:string}[], columnOrder?: string[], columnLabels?: Record<string,string>, title?: string, subtitle?: string }} options
 */
export function buildFormattedWorksheet(rows, options = {}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const { columns, columnOrder, columnLabels, title, subtitle } = options;

  let keys;
  let headers;

  if (columns && columns.length) {
    keys = columns.map((c) => c.key);
    headers = columns.map((c) => c.label || humanizeExcelHeader(c.key));
  } else {
    keys =
      columnOrder && columnOrder.length
        ? columnOrder
        : safeRows.length
        ? Object.keys(safeRows[0])
        : [];
    headers = keys.map((k) => columnLabels?.[k] || humanizeExcelHeader(k));
  }

  const body = safeRows.map((row) =>
    keys.map((k) => formatExcelCellValue(row?.[k]))
  );

  const titleRows = [];
  if (title) {
    titleRows.push([title]);
    if (subtitle) titleRows.push([subtitle]);
  }

  const headerRowIndex = titleRows.length;
  const aoa = [...titleRows, headers, ...body];
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  for (let r = 0; r < body.length; r += 1) {
    for (let c = 0; c < keys.length; c += 1) {
      const addr = XLSX.utils.encode_cell({ r: headerRowIndex + 1 + r, c });
      const typed = cellValueForSheet(keys[c], safeRows[r]?.[keys[c]]);
      if (typed.v === "") {
        if (worksheet[addr]) delete worksheet[addr];
        continue;
      }
      worksheet[addr] = typed;
    }
  }

  if (title && keys.length > 1) {
    worksheet["!merges"] = worksheet["!merges"] || [];
    for (let t = 0; t < titleRows.length; t += 1) {
      worksheet["!merges"].push({
        s: { r: t, c: 0 },
        e: { r: t, c: keys.length - 1 },
      });
    }
  }

  worksheet["!cols"] = keys.map((_, colIdx) => {
    let max = String(headers[colIdx] || "").length;
    if (titleRows[0]) max = Math.max(max, String(titleRows[0][0] || "").length);
    const sample = Math.min(body.length, 500);
    for (let r = 0; r < sample; r += 1) {
      max = Math.max(max, String(body[r][colIdx] ?? "").length);
    }
    return { wch: Math.min(Math.max(max + 2, 10), 55) };
  });

  if (keys.length) {
    const lastRow = headerRowIndex + body.length;
    worksheet["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: lastRow, c: keys.length - 1 },
      }),
    };
  }

  worksheet.__excelHeaderRow1Based = headerRowIndex + 1;
  return worksheet;
}

export async function writeWorkbookToFile(
  worksheet,
  filename,
  sheetName = "Data",
  options = {}
) {
  const workbook = XLSX.utils.book_new();
  const safeName = String(sheetName || "Data").slice(0, 31);
  XLSX.utils.book_append_sheet(workbook, worksheet, safeName);

  const headerRow1Based =
    options.headerRow1Based ||
    worksheet.__excelHeaderRow1Based ||
    1;

  const raw = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    compression: true,
  });

  const styled = await applyProfessionalXlsxPresentation(
    raw,
    headerRow1Based
  );

  saveAs(
    new Blob([styled], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename
  );
}

export async function exportObjectsToExcel(rows, filename, options = {}) {
  const ws = buildFormattedWorksheet(rows, options);
  await writeWorkbookToFile(ws, filename, options.sheetName || "Data");
}

/** Friendly headers for student grid export (exportSchema `text` keys). */
export const STUDENT_EXPORT_HEADER_LABELS = {
  ID: "ID",
  grNo: "GR No",
  Fcode: "Family Code",
  name: "Student Name",
  father_name: "Father Name",
  mother_name: "Mother Name",
  gender: "Gender",
  date_of_birth: "Date of Birth",
  religion: "Religion",
  surname: "Surname",
  contact: "Contact (Father)",
  contact_mother: "Contact (Mother)",
  contact_student: "Contact (Student)",
  address: "Address",
  rollNo: "Roll No",
  date_of_admission: "Date of Admission",
  last_institute: "Last Institute",
  class: "Class",
  class_admitted: "Class Admitted",
  fees: "Monthly Fee",
  status: "Status",
  house: "House",
  bform_number: "B-Form No",
  father_occupation: "Father Occupation",
  father_cnic: "Father CNIC",
  father_education: "Father Education",
  father_office_phone: "Father Office Phone",
  father_office_address: "Father Office Address",
  mother_occupation: "Mother Occupation",
  mother_cnic: "Mother CNIC",
  mother_education: "Mother Education",
  mother_office_phone: "Mother Office Phone",
  mother_office_address: "Mother Office Address",
  guardian_name: "Guardian Name",
  guardian_cnic: "Guardian CNIC",
  rfID: "RFID",
  date_of_leaving: "Date of Leaving",
  leaving_reason: "Leaving Reason",
  siblings: "Siblings",
  Siblings: "Siblings",
  "Siblings Class": "Siblings Class",
  "admitted campues": "Admitted Campus",
  "current campues": "Current Campus",
  campus: "Campus",
};

export const STUDENT_QUICK_EXPORT_COLUMNS = [
  { key: "gr", label: "GR No" },
  { key: "family_code", label: "Family Code" },
  { key: "name", label: "Student Name" },
  { key: "father", label: "Father Name" },
  { key: "surname", label: "Surname" },
  { key: "gender", label: "Gender" },
  { key: "contact", label: "Contact (Father)" },
  { key: "contact_mother", label: "Contact (Mother)" },
  { key: "contact_student", label: "Contact (Student)" },
  { key: "current_address", label: "Address" },
  { key: "class", label: "Class" },
  { key: "fees", label: "Monthly Fee" },
  { key: "status", label: "Status" },
  { key: "house", label: "House" },
  { key: "rfID", label: "RFID" },
];

/**
 * Stable column order for myskooltcf bulk export (matches student-sample.xlsx layout).
 * @param {string[]} sampleHeaders — e.g. TCF_BULK_SAMPLE_HEADERS from student grid
 * @param {string[]} docTitles — dynamic document column titles
 */
export function buildTcfBulkExportColumnDefinitions(
  sampleHeaders,
  docTitles = []
) {
  const headers = [...(sampleHeaders || [])];

  const scholarshipIdx = headers.indexOf("Scholarship Status");
  if (scholarshipIdx >= 0 && !headers.includes("Zakat Eligibility")) {
    headers.splice(scholarshipIdx + 1, 0, "Zakat Eligibility");
  }

  ["Current Status", "Leaving Date", "Leaving Reason"].forEach((col) => {
    if (!headers.includes(col)) {
      headers.push(col);
    }
  });

  const headerSet = new Set(headers);
  const docCols = [...(docTitles || [])]
    .filter((t) => t && !headerSet.has(t))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  return [...headers, ...docCols].map((key) => ({ key, label: key }));
}

export const STAFF_EXPORT_COLUMNS = [
  { key: "empCodeStr", label: "Employee Code" },
  { key: "name", label: "Name" },
  { key: "father_name", label: "Father / Husband Name" },
  { key: "spouse", label: "Spouse" },
  { key: "designation", label: "Designation" },
  { key: "department_name", label: "Department" },
  { key: "gender", label: "Gender" },
  { key: "contact_number", label: "Contact" },
  { key: "cnic", label: "CNIC" },
  { key: "rfID", label: "RFID" },
  { key: "date_of_birth", label: "Date of Birth" },
  { key: "appointedOn", label: "Appointed On" },
  { key: "status", label: "Status" },
  { key: "applicantStatus", label: "Applicant Status" },
  { key: "basicSalary", label: "Basic Salary" },
  { key: "bonuses", label: "Bonuses" },
  { key: "totalSalary", label: "Total Salary" },
  { key: "payMode", label: "Pay Mode" },
  { key: "accountTitle", label: "Account Title" },
  { key: "accountNumber", label: "Account Number" },
  { key: "allowedLeaves", label: "Allowed Leaves" },
  { key: "remaining_leaves", label: "Remaining Leaves" },
  { key: "remaining_sickLeaves", label: "Remaining Sick Leaves" },
  { key: "qualification", label: "Qualification" },
  { key: "experience", label: "Experience" },
  { key: "teachingLevel", label: "Teaching Level" },
  { key: "address_current", label: "Current Address" },
  { key: "address_permanent", label: "Permanent Address" },
  { key: "emergency_contact_person", label: "Emergency Contact" },
  { key: "emergency_contact_relation", label: "Emergency Relation" },
  { key: "emergency_number", label: "Emergency Number" },
  { key: "leaving_date", label: "Leaving Date" },
  { key: "rejoining_date", label: "Rejoining Date" },
  { key: "registrationDate", label: "Registration Date" },
  { key: "approvalDate", label: "Approval Date" },
  { key: "card_number", label: "Card Number" },
  { key: "note", label: "Note" },
];

/** Template for staff increment import (Staff ID, Name, Department, Amount). Date is set in the import UI. */
export const STAFF_INCREMENT_IMPORT_COLUMNS = [
  { key: "tID", label: "Staff ID" },
  { key: "name", label: "Name" },
  { key: "department", label: "Department" },
  { key: "amount", label: "Amount" },
];
